import { z } from 'zod';
import { emailSchema, uuidSchema, visuelSchema } from './common.js';
// La politique de mot de passe est UNE, définie avec l'authentification : la
// recopier ici la ferait diverger au premier ajustement.
import { passwordSchema } from './auth.js';
/**
 * Utilisateurs et rôles (W-30 pour la boutique, W-36 §12.9 pour la plateforme).
 *
 * ⭐ Un compte utilisateur est GLOBAL à la plateforme : la même personne peut
 * travailler dans plusieurs boutiques, avec un rôle différent dans chacune.
 * C'est `store_members` qui porte le rôle et le statut, jamais `users` — d'où
 * deux notions distinctes ici, `PlatformUser` et `StoreMember`, qu'il ne faut
 * pas confondre même si elles décrivent la même personne.
 */
export const memberStatusSchema = z.enum(['invited', 'active', 'suspended']);
export const MEMBER_STATUS_LABELS = {
    invited: 'Invité',
    active: 'Actif',
    suspended: 'Suspendu',
};
export const inviteMemberSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    roleId: uuidSchema,
});
export const updateMemberSchema = z
    .object({
    roleId: uuidSchema.optional(),
    status: memberStatusSchema.optional(),
})
    .refine((v) => v.roleId !== undefined || v.status !== undefined, {
    message: 'Rien à modifier',
});
export const updateRolePermissionsSchema = z.object({
    permissionIds: z.array(uuidSchema).max(200),
});
/**
 * Ordre d'affichage des ressources dans la matrice, et leur intitulé.
 *
 * Une carte explicite plutôt qu'un tri alphabétique : la matrice se lit du plus
 * courant au plus rare, comme le menu de l'application. « Paramètres » ferme la
 * marche parce que c'est la ressource qu'on accorde en dernier.
 */
export const RESOURCE_LABELS = {
    customer: 'Clients',
    item: 'Produits & Services',
    supplier: 'Fournisseurs',
    transaction: 'Transactions',
    invoice: 'Facturation',
    purchase: 'Achats',
    stock: 'Stock',
    appointment: 'Rendez-vous',
    document: 'Documents',
    task: 'Tâches',
    report: 'Rapports',
    settings: 'Paramètres',
    platform: 'Plateforme',
};
export const RESOURCE_ORDER = [
    'customer',
    'item',
    'supplier',
    'transaction',
    'invoice',
    'purchase',
    'stock',
    'appointment',
    'document',
    'task',
    'report',
    'settings',
];
/** Les quatre colonnes régulières de la matrice ; le reste va en « Spécifique ». */
export const MATRIX_ACTIONS = ['read', 'create', 'update', 'delete'];
export const ACTION_LABELS = {
    read: 'Voir',
    create: 'Créer',
    update: 'Modifier',
    delete: 'Supprimer',
};
export const createPlatformUserSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    isPlatformAdmin: z.boolean().default(false),
});
/**
 * Un portrait encode en data-URI, deja redimensionne par le navigateur.
 *
 * ⚠️ Plafond repris de `visuelSchema`, comme le logo de boutique : le portrait
 * sort du MÊME redimensionnement 640×854 que les photos d'article, donc du
 * même ordre de poids — ~95 000 caractères pour un visuel courant. Resté à
 * 65 536, ce champ refusait la plupart des portraits un peu détaillés, et
 * refuserait désormais tout portrait transparent, plus lourd depuis qu'on lui
 * conserve son canal alpha.
 *
 * Sans risque côté base : `users.avatar_url` est un TEXT sans contrainte de
 * taille — contrairement aux visuels du schéma client, dont le plafond existe
 * AUSSI en base et doit bouger avec.
 */
export const portraitSchema = visuelSchema.regex(/^data:image\/(jpeg|png|webp);base64,/, "Format d'image non pris en charge");
export const updatePlatformUserSchema = z
    .object({
    email: emailSchema.optional(),
    avatarUrl: portraitSchema.nullish(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(30).optional(),
    isPlatformAdmin: z.boolean().optional(),
    status: z.enum(['invited', 'active', 'disabled']).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Rien à modifier' });
/**
 * Ce qu’une BOUTIQUE peut modifier sur le compte d’un de ses membres.
 *
 * ⭐ Un schéma à part, et non le précédent amputé : la différence entre les
 * deux est une règle de sécurité, pas un détail de formulaire. L’écrire ici
 * la rend lisible — et impossible à contourner par un appel forgé.
 *
 * ⚠️ `isPlatformAdmin` en est ABSENT, délibérément. Un gérant qui pourrait se
 * nommer administrateur de la plateforme depuis l’écran de son équipe
 * prendrait la main sur l’installation entière : c’est le chemin d’escalade
 * le plus court qui soit, et il ne doit pas exister.
 */
export const updateMemberAccountSchema = z
    .object({
    email: emailSchema.optional(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(30).optional(),
    status: z.enum(['invited', 'active', 'disabled']).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Rien à modifier' });
export const creerCompteSchema = z.object({
    email: emailSchema,
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(30).optional(),
    roleId: uuidSchema,
    /**
     * ⚠️ FACULTATIF, et c’est un choix : sans mot de passe le compte reste
     * « invité » et ne peut pas entrer. Le gérant en pose un quand il remet
     * l’accès en main propre — ce qui est le cas ordinaire au comptoir.
     */
    password: passwordSchema.optional(),
});
export const majCompteSchema = z
    .object({
    email: emailSchema.optional(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(30).optional(),
    roleId: uuidSchema.optional(),
    status: z.enum(['invited', 'active', 'disabled']).optional(),
    password: passwordSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Rien à modifier' });
// ------------------------------------------------- son propre compte (W-34)
export const updateProfileSchema = z.object({
    avatarUrl: portraitSchema.nullish(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(30).optional(),
    email: emailSchema.optional(),
});
/**
 * Changement de mot de passe par l'interesse lui-meme.
 *
 * L'ancien mot de passe est EXIGE meme si la session est valide : une session
 * ouverte sur un poste laisse sans surveillance ne doit pas suffire a s'emparer
 * definitivement du compte.
 */
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Mot de passe actuel requis'),
    newPassword: z
        .string()
        .min(12, 'Au moins 12 caracteres')
        .max(200)
        .regex(/[a-z]/, 'Au moins une minuscule')
        .regex(/[A-Z]/, 'Au moins une majuscule')
        .regex(/[0-9]/, 'Au moins un chiffre'),
    confirmation: z.string(),
})
    .refine((v) => v.newPassword === v.confirmation, {
    message: 'La confirmation ne correspond pas',
    path: ['confirmation'],
})
    .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'Le nouveau mot de passe doit differer de l ancien',
    path: ['newPassword'],
});
//# sourceMappingURL=team.js.map